import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, MapPin, Video, Phone, MessageSquare,
  User, Package, FileText, Save, Check, Calendar, Clock, X,
} from 'lucide-react';
import { clientsApi, sessionsApi, packagesApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import './Pages.css';

// ============================================================
// Constants
// ============================================================

const SESSION_TYPES = [
  { value: 'erstgespraech', label: 'Erstgespräch' },
  { value: 'folgesitzung', label: 'Folgesitzung' },
  { value: 'checkin', label: 'Check-in' },
  { value: 'abschluss', label: 'Abschlussgespräch' },
  { value: 'supervision', label: 'Supervision' },
  { value: 'workshop', label: 'Workshop' },
];

const FORMATS: { value: string; label: string; Icon: React.ElementType }[] = [
  { value: 'in_person', label: 'Vor Ort', Icon: MapPin },
  { value: 'online', label: 'Video (Zoom / Teams)', Icon: Video },
  { value: 'phone', label: 'Telefon', Icon: Phone },
  { value: 'chat', label: 'Chat / Async', Icon: MessageSquare },
];

const STATUSES = [
  { value: 'scheduled', label: 'Geplant', dot: '#22c55e' },
  { value: 'confirmed', label: 'Bestätigt', dot: '#3b82f6' },
  { value: 'cancelled', label: 'Abgesagt', dot: '#ef4444' },
];

// ============================================================
// Helpers
// ============================================================

function getInitials(first: string, last: string) {
  return `${(first || '?').charAt(0)}${(last || '?').charAt(0)}`.toUpperCase();
}

function initialsColor(name: string) {
  const colors = ['#B07468', '#6B8E6B', '#6B7E8E', '#8E6B7E', '#7E8E6B', '#8E7E6B'];
  let h = 0;
  for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtMonth(iso: string) {
  return new Date(iso).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}

// ============================================================
// Types
// ============================================================

interface ClientOption {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  created_at: string;
  status: string;
}

interface ClientPkg {
  id: string;
  package_name: string;
  client_name: string;
  sessions_total: number;
  sessions_used: number;
  sessions_remaining: number;
  status: string;
  valid_until: string | null;
}

interface SessionInfo {
  id: string;
  session_number: number;
  scheduled_at: string | null;
  session_type: string;
  duration_minutes: number | null;
  status: string;
}

// ============================================================
// Avatar Component
// ============================================================

function Avatar({ first, last, size = 40 }: { first: string; last: string; size?: number }) {
  const bg = initialsColor(`${first} ${last}`);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: bg,
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 600, fontSize: size * 0.38, flexShrink: 0,
    }}>
      {getInitials(first, last)}
    </div>
  );
}

// ============================================================
// Section Header
// ============================================================

function SectionHeader({ num, title, sub }: { num: number; title: string; sub?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: 'var(--primary)',
        display: 'inline-block', flexShrink: 0,
      }} />
      <span style={{ fontWeight: 700, fontSize: '0.82rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {num} — {title}
      </span>
      {sub && (
        <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontWeight: 400, fontStyle: 'italic' }}>
          {sub}
        </span>
      )}
    </div>
  );
}

// ============================================================
// Chip Selector
// ============================================================

function ChipSelect({ options, value, onChange, renderLabel }: {
  options: { value: string; label: string; [k: string]: any }[];
  value: string;
  onChange: (v: string) => void;
  renderLabel?: (opt: any, active: boolean) => React.ReactNode;
}) {
  return (
    <div className="category-chips">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            className={`chip ${active ? 'chip-active' : ''}`}
            onClick={() => onChange(opt.value)}
          >
            {renderLabel ? renderLabel(opt, active) : opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================
// Main Page Component
// ============================================================

export default function SitzungAnlegen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const { user } = useAuth();
  const preselectedClientId = searchParams.get('client');

  // --- Form State ---
  const [form, setForm] = useState({
    client_id: '',
    date: '',
    time: '10:00',
    duration_minutes: 60,
    session_type: 'folgesitzung',
    location_type: 'in_person',
    location_address: '',
    status: 'scheduled',
    package_id: '',
    agenda: '',
    interne_notiz: '',
    is_billable: true,
  });

  // --- Client Search ---
  const [clientQuery, setClientQuery] = useState('');
  const [clientResults, setClientResults] = useState<ClientOption[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // --- Sidebar Data ---
  const [clientSessions, setClientSessions] = useState<SessionInfo[]>([]);
  const [clientPackages, setClientPackages] = useState<ClientPkg[]>([]);
  const [sessionCount, setSessionCount] = useState(0);

  // --- UI State ---
  const [saving, setSaving] = useState(false);

  // === Client Search Debounced ===
  useEffect(() => {
    if (!clientQuery.trim() || clientQuery.length < 2) {
      setClientResults([]);
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setClientLoading(true);
      try {
        const res = await clientsApi.list({ search: clientQuery, limit: 8 });
        setClientResults(res.data.items || []);
      } catch {
        setClientResults([]);
      }
      setClientLoading(false);
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [clientQuery]);

  // === Close dropdown on outside click ===
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // === Pre-select client from URL param ===
  useEffect(() => {
    if (preselectedClientId) {
      clientsApi.get(preselectedClientId).then((res) => {
        selectClient(res.data);
      }).catch(() => {});
    }
  }, [preselectedClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // === Restore draft from localStorage ===
  useEffect(() => {
    if (preselectedClientId) return; // Will be loaded via client preselect
    const draft = localStorage.getItem('sitzung_entwurf_neu');
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        setForm((f) => ({ ...f, ...parsed, client_id: f.client_id }));
      } catch { /* ignore invalid JSON */ }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // === Load sidebar data when client selected ===
  const loadClientData = useCallback(async (clientId: string) => {
    try {
      const [sessRes, pkgRes] = await Promise.all([
        sessionsApi.list({ client_id: clientId } as any),
        packagesApi.activeForClient(clientId),
      ]);
      const sessions = sessRes.data.items || sessRes.data || [];
      setClientSessions(sessions);
      setSessionCount(sessRes.data.total || sessions.length);
      setClientPackages(pkgRes.data || []);
    } catch {
      setClientSessions([]);
      setClientPackages([]);
    }
  }, []);

  const selectClient = (client: ClientOption) => {
    setSelectedClient(client);
    // Restore draft for this client if exists
    const draft = localStorage.getItem(`sitzung_entwurf_${client.id}`);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        setForm((f) => ({ ...f, ...parsed, client_id: client.id }));
        showToast('Entwurf wiederhergestellt', 'info');
      } catch {
        setForm((f) => ({ ...f, client_id: client.id, package_id: '' }));
      }
    } else {
      setForm((f) => ({ ...f, client_id: client.id, package_id: '' }));
    }
    setClientQuery(`${client.first_name} ${client.last_name}`);
    setShowClientDropdown(false);
    loadClientData(client.id);
  };

  const clearClient = () => {
    setSelectedClient(null);
    setForm((f) => ({ ...f, client_id: '', package_id: '' }));
    setClientQuery('');
    setClientSessions([]);
    setClientPackages([]);
    setSessionCount(0);
  };

  // === Computed ===
  const selectedPkg = clientPackages.find((p) => p.id === form.package_id);
  const remainingAfter = selectedPkg ? selectedPkg.sessions_remaining - 1 : null;
  const lastSession = clientSessions.length > 0
    ? clientSessions.reduce((a, b) => (a.scheduled_at || '') > (b.scheduled_at || '') ? a : b)
    : null;
  const totalPkgRemaining = clientPackages.reduce((sum, p) => sum + p.sessions_remaining, 0);

  const computedEnd = () => {
    if (!form.time || !form.duration_minutes) return '';
    const [h, m] = form.time.split(':').map(Number);
    const total = h * 60 + m + form.duration_minutes;
    const eh = Math.floor(total / 60) % 24;
    const em = total % 60;
    return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
  };

  const sessionTypeLabel = SESSION_TYPES.find((t) => t.value === form.session_type)?.label || form.session_type;
  const formatLabel = FORMATS.find((f) => f.value === form.location_type)?.label || form.location_type;
  const FormatIcon = FORMATS.find((f) => f.value === form.location_type)?.Icon || MapPin;

  // === Submit ===
  const handleSubmit = async () => {
    if (!form.client_id) {
      showToast('Bitte einen Klienten auswählen', 'info');
      return;
    }
    if (!form.date) {
      showToast('Bitte ein Datum auswählen', 'info');
      return;
    }
    if (form.location_type === 'in_person' && !form.location_address.trim()) {
      showToast('Bitte eine Adresse für die Vor-Ort-Sitzung angeben', 'info');
      return;
    }
    if (form.duration_minutes < 15 || form.duration_minutes > 480) {
      showToast('Sitzungsdauer muss zwischen 15 und 480 Minuten liegen', 'info');
      return;
    }

    // Prüfe ob Datum in der Vergangenheit liegt
    const scheduledDate = new Date(`${form.date}T${form.time}`);
    const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000);
    if (scheduledDate < fiveMinAgo) {
      showToast('Sitzung kann nicht in der Vergangenheit geplant werden', 'info');
      return;
    }

    // Warnung: Letzte Sitzung im Paket
    if (selectedPkg && selectedPkg.sessions_remaining === 1) {
      const confirmed = window.confirm(
        `Das ist die letzte Sitzung im Paket "${selectedPkg.package_name}". Nach dem Speichern ist das Paketguthaben aufgebraucht. Fortfahren?`
      );
      if (!confirmed) return;
    }

    // Warnung: Paket hat keine Sitzungen mehr
    if (selectedPkg && selectedPkg.sessions_remaining <= 0) {
      showToast(`Keine Sitzungen mehr im Paket "${selectedPkg.package_name}" verfügbar`, 'error');
      return;
    }

    setSaving(true);
    try {
      const scheduledAt = form.date && form.time
        ? new Date(`${form.date}T${form.time}`).toISOString()
        : undefined;

      const contentData: any = {};
      if (form.agenda) contentData.agenda = form.agenda;
      if (form.interne_notiz) contentData.vorb_intention = form.interne_notiz;

      await sessionsApi.create({
        client_id: form.client_id,
        scheduled_at: scheduledAt,
        duration_minutes: form.duration_minutes || undefined,
        session_type: form.session_type,
        location_type: form.location_type,
        location_address: form.location_address || undefined,
        status: form.status,
        package_id: form.package_id || undefined,
        is_billable: form.is_billable,
        content_data: Object.keys(contentData).length > 0 ? contentData : undefined,
      });

      // Entwurf nach erfolgreichem Erstellen aus localStorage entfernen
      localStorage.removeItem(`sitzung_entwurf_${form.client_id}`);

      showToast('Sitzung wurde angelegt', 'success');
      navigate(form.client_id ? `/klienten/${form.client_id}` : '/sitzungen');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : 'Fehler beim Erstellen', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDraft = () => {
    const draftKey = form.client_id ? `sitzung_entwurf_${form.client_id}` : 'sitzung_entwurf_neu';
    localStorage.setItem(draftKey, JSON.stringify(form));
    showToast('Entwurf lokal gespeichert', 'success');
  };

  const coachName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Coach';

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div>
      {/* Page Header */}
      <div style={{
        background: 'var(--primary)', color: 'white', borderRadius: 'var(--radius)',
        padding: '20px 28px', marginBottom: 28,
      }}>
        <div style={{ fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.8, marginBottom: 4 }}>
          CoachDesk365 &middot; Sitzungsmanagement
        </div>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>Neue Sitzung anlegen</h1>
        <div style={{ fontSize: '0.84rem', opacity: 0.85, marginTop: 4 }}>
          Strukturierter Erfassungsflow — Klient zuerst, dann Details
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="sitzung-anlegen-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 28, alignItems: 'start' }}>

        {/* === LEFT: Form === */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

          {/* Section 1 — KLIENT */}
          <div className="card" style={{ padding: '20px 24px' }}>
            <SectionHeader num={1} title="Klient" />
            <label style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 8, display: 'block' }}>
              Klient auswählen <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <div ref={dropdownRef} style={{ position: 'relative' }}>
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{
                  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                }} />
                <input
                  type="text"
                  value={clientQuery}
                  onChange={(e) => {
                    setClientQuery(e.target.value);
                    setShowClientDropdown(true);
                    if (selectedClient) clearClient();
                  }}
                  onFocus={() => { if (clientQuery.length >= 2) setShowClientDropdown(true); }}
                  placeholder="Name oder E-Mail eingeben..."
                  style={{ paddingLeft: 36, width: '100%' }}
                />
                {selectedClient && (
                  <button
                    type="button"
                    className="btn-icon"
                    onClick={clearClient}
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Dropdown Results */}
              {showClientDropdown && (clientResults.length > 0 || clientLoading) && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)', marginTop: 4, maxHeight: 280, overflowY: 'auto',
                  boxShadow: 'var(--shadow)',
                }}>
                  {clientLoading && (
                    <div style={{ padding: '12px 16px', color: 'var(--text-muted)', fontSize: '0.84rem' }}>
                      Suche...
                    </div>
                  )}
                  {clientResults.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => selectClient(c)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                        cursor: 'pointer', transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <Avatar first={c.first_name} last={c.last_name} size={36} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                          {c.first_name} {c.last_name}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                          {c.email || 'Keine E-Mail'}
                          {c.created_at && <> &middot; seit {fmtMonth(c.created_at)}</>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Section 2 — DATUM & ZEIT */}
          <div className="card" style={{ padding: '20px 24px' }}>
            <SectionHeader num={2} title="Datum & Zeit" />
            <div className="form-row">
              <div className="form-group">
                <label><Calendar size={14} style={{ marginRight: 4, verticalAlign: -2 }} />Datum</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label><Clock size={14} style={{ marginRight: 4, verticalAlign: -2 }} />Uhrzeit</label>
                <input
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Dauer</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number"
                    value={form.duration_minutes}
                    onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })}
                    min={15}
                    step={15}
                    style={{ width: 80 }}
                  />
                  <span style={{ fontSize: '0.84rem', color: 'var(--text-muted)' }}>Minuten</span>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3 — TYP & FORMAT */}
          <div className="card" style={{ padding: '20px 24px' }}>
            <SectionHeader num={3} title="Typ & Format" />

            <label style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 8, display: 'block' }}>
              Sitzungstyp <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <ChipSelect
              options={SESSION_TYPES}
              value={form.session_type}
              onChange={(v) => setForm({ ...form, session_type: v })}
            />

            <label style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 8, display: 'block', marginTop: 20 }}>
              Format
            </label>
            <ChipSelect
              options={FORMATS}
              value={form.location_type}
              onChange={(v) => setForm({ ...form, location_type: v })}
              renderLabel={(opt, active) => (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <opt.Icon size={14} />
                  {opt.label}
                </span>
              )}
            />

            <div className="form-group" style={{ marginTop: 16 }}>
              <label>
                {form.location_type === 'in_person' ? (
                  <>Adresse / Ort <span style={{ color: 'var(--danger)' }}>*</span></>
                ) : (
                  <>Meeting-Link / Ort <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontWeight: 400 }}>optional</span></>
                )}
              </label>
              <input
                type="text"
                value={form.location_address}
                onChange={(e) => setForm({ ...form, location_address: e.target.value })}
                placeholder={form.location_type === 'in_person'
                  ? 'z.B. Praxis Musterstr. 1, Raum 2'
                  : form.location_type === 'online'
                    ? 'z.B. Zoom-Link oder Teams-Link'
                    : 'Ort oder Link eingeben'}
                style={form.location_type === 'in_person' && !form.location_address.trim()
                  ? { borderColor: 'var(--warning, #f59e0b)' }
                  : undefined}
              />
              {form.location_type === 'in_person' && !form.location_address.trim() && (
                <div style={{ fontSize: '0.76rem', color: 'var(--warning, #f59e0b)', marginTop: 4 }}>
                  Adresse wird für Vor-Ort-Sitzungen benötigt
                </div>
              )}
            </div>
          </div>

          {/* Section 4 — STATUS & ZUORDNUNG */}
          <div className="card" style={{ padding: '20px 24px' }}>
            <SectionHeader num={4} title="Status & Zuordnung" />

            <div className="form-row" style={{ alignItems: 'flex-start' }}>
              <div className="form-group">
                <label style={{ fontWeight: 600, fontSize: '0.88rem' }}>Status</label>
                <ChipSelect
                  options={STATUSES}
                  value={form.status}
                  onChange={(v) => setForm({ ...form, status: v })}
                  renderLabel={(opt, active) => (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: active ? '#fff' : opt.dot,
                        display: 'inline-block',
                      }} />
                      {opt.label}
                    </span>
                  )}
                />
              </div>
              <div className="form-group">
                <label style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                  Coach / Mitarbeiter
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6 }}>Team-Plan</span>
                </label>
                <input type="text" value={`${coachName} (ich)`} disabled style={{ opacity: 0.7 }} />
              </div>
            </div>
          </div>

          {/* Section 5 — COACHING-PAKET VERKNÜPFEN */}
          <div className="card" style={{ padding: '20px 24px' }}>
            <SectionHeader
              num={5}
              title="Coaching-Paket verknüpfen"
              sub="optional — zieht automatisch 1 Sitzung vom Guthaben ab"
            />

            {!selectedClient ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.84rem', fontStyle: 'italic' }}>
                Bitte zuerst einen Klienten auswählen.
              </div>
            ) : clientPackages.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.84rem' }}>
                Keine aktiven Pakete für diesen Klienten vorhanden.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {clientPackages.map((pkg) => {
                  const isSelected = form.package_id === pkg.id;
                  return (
                    <div
                      key={pkg.id}
                      onClick={() => setForm({ ...form, package_id: isSelected ? '' : pkg.id })}
                      style={{
                        border: `2px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                        borderRadius: 'var(--radius)',
                        padding: '14px 18px',
                        cursor: 'pointer',
                        background: isSelected ? 'var(--primary-bg)' : 'var(--bg-card)',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Package size={16} color="var(--primary)" />
                            <span style={{ fontWeight: 700, fontSize: '0.92rem' }}>{pkg.package_name}</span>
                          </div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
                            {pkg.client_name} &middot; {pkg.sessions_total} Sitzungen
                          </div>
                        </div>
                        <div style={{
                          fontWeight: 700, fontSize: '0.92rem',
                          color: pkg.sessions_remaining <= 2 ? 'var(--danger)' : 'var(--success)',
                        }}>
                          {pkg.sessions_remaining} verbleibend
                        </div>
                      </div>

                      {isSelected && (
                        <div style={{
                          marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)',
                          background: 'var(--bg)', fontSize: '0.82rem', color: 'var(--text-light)',
                          display: 'flex', alignItems: 'center', gap: 6,
                        }}>
                          <span style={{ fontSize: '1rem' }}>&#9889;</span>
                          Nach dem Speichern wird 1 Sitzung automatisch vom Paketguthaben abgezogen
                          ({pkg.sessions_remaining} &rarr; {pkg.sessions_remaining - 1}).
                        </div>
                      )}
                    </div>
                  );
                })}

                {form.package_id && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, package_id: '' })}
                    style={{
                      background: 'none', border: 'none', color: 'var(--primary)',
                      cursor: 'pointer', fontSize: '0.84rem', textAlign: 'left', padding: '4px 0',
                    }}
                  >
                    + Kein Paket verknüpfen (Einzelabrechnung)
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Section 6 — ABRECHNUNG */}
          <div className="card" style={{ padding: '20px 24px' }}>
            <SectionHeader num={6} title="Abrechnung" />
            <label style={{
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
              fontSize: '0.88rem', fontWeight: 500,
            }}>
              <input
                type="checkbox"
                checked={form.is_billable}
                onChange={(e) => setForm({ ...form, is_billable: e.target.checked })}
                style={{ width: 18, height: 18, accentColor: 'var(--primary)' }}
              />
              Abrechenbar (erscheint auf Rechnung)
            </label>
            {!form.is_billable && (
              <div style={{
                marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)',
                background: 'var(--bg)', fontSize: '0.82rem', color: 'var(--text-muted)',
              }}>
                Diese Sitzung wird nicht auf der Rechnung aufgeführt (z.B. Kennenlerngespräch, interne Sitzung).
              </div>
            )}
          </div>

          {/* Section 7 — VORBEREITUNG */}
          <div className="card" style={{ padding: '20px 24px' }}>
            <SectionHeader num={7} title="Vorbereitung" />

            <div className="form-group">
              <label>
                Agenda / Themen
                <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6 }}>optional</span>
              </label>
              <textarea
                value={form.agenda}
                onChange={(e) => setForm({ ...form, agenda: e.target.value })}
                placeholder="Was soll in dieser Sitzung besprochen werden? Was nimmt der/die Klient·in mit?"
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>
                Interne Notiz
                <span style={{
                  fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6,
                  background: 'var(--bg)', padding: '2px 8px', borderRadius: 10,
                }}>
                  nur für dich sichtbar
                </span>
              </label>
              <textarea
                value={form.interne_notiz}
                onChange={(e) => setForm({ ...form, interne_notiz: e.target.value })}
                placeholder="Eigene Erinnerungen, Vorbereitung, Hypothesen..."
                rows={3}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex', gap: 12, alignItems: 'center', padding: '20px 0',
            borderTop: '1px solid var(--border-light)',
          }}>
            <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
              Abbrechen
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleDraft}>
              <Save size={16} /> Als Entwurf speichern
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={saving || !form.client_id}
              style={{ marginLeft: 'auto' }}
            >
              {saving ? 'Wird erstellt...' : <><Check size={16} /> Sitzung anlegen</>}
            </button>
          </div>
        </div>

        {/* === RIGHT: Sidebar === */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, position: 'sticky', top: 24 }}>

          {/* Klient-Vorschau */}
          <div className="card" style={{ padding: '20px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
              fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              <User size={16} color="var(--primary)" /> Klient-Vorschau
            </div>

            {!selectedClient ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: '0.84rem' }}>
                Klient wird hier angezeigt, sobald du einen auswählst.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <Avatar first={selectedClient.first_name} last={selectedClient.last_name} size={48} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>
                      {selectedClient.first_name} {selectedClient.last_name}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      Klient seit {fmtMonth(selectedClient.created_at)}
                    </div>
                  </div>
                </div>

                {/* Stats Grid */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12,
                }}>
                  <div style={{
                    textAlign: 'center', padding: '10px 8px',
                    background: 'var(--bg)', borderRadius: 'var(--radius-sm)',
                  }}>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{sessionCount}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Sitzungen</div>
                  </div>
                  <div style={{
                    textAlign: 'center', padding: '10px 8px',
                    background: 'var(--bg)', borderRadius: 'var(--radius-sm)',
                  }}>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem', color: totalPkgRemaining > 0 ? 'var(--primary)' : 'var(--text-muted)' }}>
                      {totalPkgRemaining}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Paket-Rest</div>
                  </div>
                </div>

                {/* Last session */}
                {lastSession && lastSession.scheduled_at && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                    Letzte Sitzung: {fmtDate(lastSession.scheduled_at)}
                    {' '}&middot; {SESSION_TYPES.find((t) => t.value === lastSession.session_type)?.label || lastSession.session_type}
                    {lastSession.duration_minutes && <> &middot; {lastSession.duration_minutes} Min</>}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Zusammenfassung */}
          <div className="card" style={{ padding: '20px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
              fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              <FileText size={16} color="var(--primary)" /> Zusammenfassung
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: '0.86rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-light)' }}>Klient</span>
                <span style={{ fontWeight: 600 }}>
                  {selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : '—'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-light)' }}>Datum</span>
                <span style={{ fontWeight: 600 }}>
                  {form.date ? fmtDate(form.date + 'T00:00') : '—'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-light)' }}>Zeit</span>
                <span style={{ fontWeight: 600 }}>
                  {form.time ? `${form.time} – ${computedEnd()} Uhr` : '—'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-light)' }}>Typ</span>
                <span style={{ fontWeight: 600 }}>{sessionTypeLabel}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-light)' }}>Format</span>
                <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FormatIcon size={14} /> {formatLabel}
                </span>
              </div>
              {selectedPkg && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-light)' }}>Paket</span>
                    <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{selectedPkg.package_name}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-light)' }}>Guthaben nach</span>
                    <span style={{ fontWeight: 700, color: remainingAfter !== null && remainingAfter <= 2 ? 'var(--danger)' : 'var(--success)' }}>
                      {remainingAfter} Sitzungen
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Nach dem Speichern */}
          <div className="card" style={{ padding: '20px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
              fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
            }}>
              <span style={{ fontSize: '1rem' }}>&#128197;</span> Nach dem Speichern
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.84rem' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <Check size={16} color="var(--success)" style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Termin erscheint im Kalender &amp; springt zur neuen Sitzung</span>
              </div>
              {selectedPkg && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Check size={16} color="var(--success)" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>1 Sitzung wird vom Paketguthaben abgezogen</span>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <Check size={16} color="var(--success)" style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Klient erhält optional eine Bestätigungs-E-Mail</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <Check size={16} color="var(--success)" style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Sitzungsnotiz-Formular wird automatisch vorbereitet</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', color: 'var(--text-muted)' }}>
                <span style={{ flexShrink: 0, marginTop: 1 }}>&rarr;</span>
                <span>Klientenprofil zeigt neue Sitzung in der Historie</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
